import moment from 'moment';
import { Query } from 'redux-little-router';
import { createSelector, OutputSelector } from 'reselect';

import { getLogCriteriaContainers } from '../declarations/log-filters/log-filters';
import { getLogColumns } from '../declarations/log/log';
import { LogSourceHelper } from '../service/LogSourceHelper/LogSourceHelper';
import { ColumnsSettingsViewDict, DateRangePreset } from '../typings/common';
import { CriteriaContainer, FilterPartialExpression } from '../typings/filter';
import { PeriodFilterValue } from '../typings/filters';
import { SortDirection } from '../typings/grid';
import { LogParams, LogScreenUrlSearchParams, LogSourceParams } from '../typings/log';
import { ClientFeatures, State } from '../typings/state';

interface MinMaxDate {
    minDate?: moment.Moment;
    maxDate?: moment.Moment;
}

export const selectLogSourceParams = createSelector(
    (state: State) => state.router.query,
    (state: State) => state.commonData.login,
    (state: State) => state.log.logParams,
    (query: Query = {}, login: string, logParams: LogParams): LogSourceParams => {
        const sortDirection = query[LogScreenUrlSearchParams.sortDirection],
            cid = query[LogScreenUrlSearchParams.cid],
            cids = cid ? cid.split(', ') : undefined,
            filter = LogSourceHelper.getFilter(query[LogScreenUrlSearchParams.filter] || '', {
                ...logParams,
                cids: cids
            });

        const dateFrom = query[LogScreenUrlSearchParams.dateFrom];
        const dateTo = query[LogScreenUrlSearchParams.dateTo];
        const preset = LogSourceHelper.normalizeParams<DateRangePreset>(
            query[LogScreenUrlSearchParams.datePreset],
            DateRangePreset
        );

        return {
            period: {
                dateFrom,
                dateTo,
                preset
            },
            filter: filter,
            order: sortDirection as SortDirection,
            clientLogin: login
        };
    }
);

/* Возвращает минимальную и максимальную даты выбора периода логгера */
export const selectLogPeriodLimits = (
    refDate: moment.Moment
): OutputSelector<State, MinMaxDate, (res: LogSourceParams) => MinMaxDate> => {
    return createSelector(
        selectLogSourceParams,
        (sourceParams: LogSourceParams): MinMaxDate => {
            if (!sourceParams) {
                return {};
            }

            return {
                minDate: refDate
                    .clone()
                    // значение в 1 год взято с потолка
                    // обсудить с бэкэндом настоящую цифру
                    .subtract(1, 'years')
                    .date(1),
                maxDate: refDate.clone()
            };
        }
    );
};

export const selectLogFilterContainers = createSelector(
    selectLogSourceParams,
    (params?: LogSourceParams): CriteriaContainer[] => {
        if (!params) {
            return [];
        }
        return getLogCriteriaContainers();
    }
);

export const selectLogFilterExpressions = createSelector(
    selectLogSourceParams,
    (sourceParams: LogSourceParams): FilterPartialExpression[] => {
        if (!sourceParams || !sourceParams.filter) {
            return [];
        }

        return sourceParams.filter;
    }
);

export const selectLogPeriodFilterValue = createSelector(
    selectLogSourceParams,
    (sourceParams: LogSourceParams): PeriodFilterValue => {
        if (!sourceParams) {
            return {
                dateRangePreset: DateRangePreset.allTime
            };
        }

        const {
            period: { dateFrom, dateTo, preset }
        } = sourceParams;

        if (dateFrom && dateTo) {
            return {
                dateFrom: moment(dateFrom),
                dateTo: moment(dateTo)
            };
        } else {
            return {
                dateRangePreset: preset || DateRangePreset.allTime
            };
        }
    }
);

export const selectLogsColumns = createSelector(
    (state: State) => state.log.login,
    (state: State) => state.log.rowExpanded,
    (state: State) => state.log.rowsCanExpand,
    (state: State) => state.log.columnsSettings,
    (state: State) => state.log.maxHeightCleared,
    (state: State) => state.commonData.features,
    (
        login: string,
        rowExpanded: Map<number, boolean>,
        rowsCanExpand: Map<number, boolean>,
        columnsSettings: ColumnsSettingsViewDict,
        maxHeightCleared: boolean,
        features: ClientFeatures
    ) => {
        return getLogColumns(rowExpanded, rowsCanExpand, columnsSettings, login, maxHeightCleared, features);
    }
);
